<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'

/**
 * Lightweight WYSIWYG editor — contenteditable div plus a toolbar that
 * fires the corresponding `document.execCommand` for each common
 * formatting action. Emits the resulting innerHTML through `v-model`
 * exactly the way `TextAreaInput` does, so it drops into existing forms
 * without any plumbing change beyond the import + tag swap.
 *
 * Why not a heavyweight editor (TipTap / Quill / CKEditor)? They'd be
 * the right call if the policy fields ever grew tables, image upload,
 * collaborative cursors, etc. — but today we only need basic prose
 * (headings, lists, links, bold/italic/underline) and zero new npm
 * dependencies. execCommand is technically deprecated but every
 * browser still ships it; we'll graduate to TipTap the day we need
 * something it can't do.
 */

const props = defineProps<{
  modelValue: string | null | undefined
  label?: string
  readonly?: boolean
  error?: string | null
  placeholder?: string
  minHeight?: number
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'blur'): void
}>()

const editorRef = ref<HTMLDivElement | null>(null)
const isFocused = ref(false)

// Internal flag so the watcher reflecting props.modelValue → DOM doesn't
// fight the input handler going DOM → props.modelValue. Without it,
// every keystroke triggers a re-write of innerHTML that resets the
// caret to position 0.
let suppressInputEcho = false

function syncFromProp() {
  if (!editorRef.value) return
  const incoming = props.modelValue ?? ''
  if (editorRef.value.innerHTML !== incoming) {
    suppressInputEcho = true
    editorRef.value.innerHTML = incoming
    suppressInputEcho = false
  }
}

watch(() => props.modelValue, () => {
  // Only re-sync the DOM if the change came from outside the editor —
  // an external setFormData/reset call. While the user is typing, the
  // input handler already keeps the DOM authoritative.
  if (!isFocused.value) syncFromProp()
})

onMounted(syncFromProp)

function handleInput() {
  if (suppressInputEcho || !editorRef.value) return
  emit('update:modelValue', editorRef.value.innerHTML)
}

function exec(command: string, value?: string) {
  if (props.readonly) return
  editorRef.value?.focus()
  document.execCommand(command, false, value)
  if (editorRef.value) emit('update:modelValue', editorRef.value.innerHTML)
}

function makeLink() {
  if (props.readonly) return
  const url = window.prompt('Link URL', 'https://')
  if (!url) return
  exec('createLink', url)
}

function clearFormatting() {
  exec('removeFormat')
  exec('unlink')
}

function onBlur() {
  isFocused.value = false
  emit('blur')
}
</script>

<template>
  <div>
    <label
      v-if="label"
      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
    >{{ label }}</label>

    <div
      class="rounded-lg border bg-white dark:bg-gray-900"
      :class="[
        error
          ? 'border-red-300 dark:border-red-700'
          : 'border-gray-200 dark:border-gray-700',
        readonly ? 'bg-gray-50 dark:bg-gray-800' : '',
      ]"
    >
      <!-- Toolbar -->
      <div
        v-if="!readonly"
        class="flex flex-wrap items-center gap-1 px-2 py-1 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-t-lg"
      >
        <button type="button" title="Bold" class="rt-btn font-bold" @mousedown.prevent @click="exec('bold')">B</button>
        <button type="button" title="Italic" class="rt-btn italic" @mousedown.prevent @click="exec('italic')">I</button>
        <button type="button" title="Underline" class="rt-btn underline" @mousedown.prevent @click="exec('underline')">U</button>
        <span class="mx-1 h-4 w-px bg-gray-300 dark:bg-gray-600"></span>
        <button type="button" title="Heading" class="rt-btn font-semibold" @mousedown.prevent @click="exec('formatBlock', 'H3')">H</button>
        <button type="button" title="Paragraph" class="rt-btn" @mousedown.prevent @click="exec('formatBlock', 'P')">P</button>
        <span class="mx-1 h-4 w-px bg-gray-300 dark:bg-gray-600"></span>
        <button type="button" title="Bullet list" class="rt-btn" @mousedown.prevent @click="exec('insertUnorderedList')">• ≡</button>
        <button type="button" title="Numbered list" class="rt-btn" @mousedown.prevent @click="exec('insertOrderedList')">1. ≡</button>
        <span class="mx-1 h-4 w-px bg-gray-300 dark:bg-gray-600"></span>
        <button type="button" title="Insert link" class="rt-btn" @mousedown.prevent @click="makeLink">🔗</button>
        <button type="button" title="Remove link" class="rt-btn" @mousedown.prevent @click="exec('unlink')">⛓̸</button>
        <span class="mx-1 h-4 w-px bg-gray-300 dark:bg-gray-600"></span>
        <button type="button" title="Clear formatting" class="rt-btn" @mousedown.prevent @click="clearFormatting">⨯</button>
      </div>

      <!-- Editable surface -->
      <div
        ref="editorRef"
        class="px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none rt-content"
        :class="[ readonly ? 'cursor-not-allowed' : '' ]"
        :contenteditable="!readonly"
        :style="{ minHeight: (minHeight ?? 140) + 'px' }"
        :data-placeholder="placeholder ?? 'Type or paste HTML…'"
        spellcheck="true"
        @input="handleInput"
        @focus="isFocused = true"
        @blur="onBlur"
      ></div>
    </div>

    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
  </div>
</template>

<style scoped>
.rt-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 26px;
  padding: 0 6px;
  font-size: 0.75rem;
  border-radius: 4px;
  color: rgb(55 65 81);
  border: 1px solid transparent;
}
.rt-btn:hover {
  background-color: rgb(229 231 235);
}
.dark .rt-btn {
  color: rgb(229 231 235);
}
.dark .rt-btn:hover {
  background-color: rgb(55 65 81);
}

.rt-content :deep(h1),
.rt-content :deep(h2),
.rt-content :deep(h3) {
  font-weight: 600;
  margin: 0.4em 0;
}
.rt-content :deep(h3) { font-size: 1.05rem; }
.rt-content :deep(ul) { list-style: disc; padding-left: 1.5rem; margin: 0.4em 0; }
.rt-content :deep(ol) { list-style: decimal; padding-left: 1.5rem; margin: 0.4em 0; }
.rt-content :deep(a) { color: rgb(37 99 235); text-decoration: underline; }
.rt-content :deep(p) { margin: 0.3em 0; }

/* Empty-state placeholder. Caret-position-safe: relies on the
   contenteditable being :empty rather than emptying the DOM ourselves. */
.rt-content:empty::before {
  content: attr(data-placeholder);
  color: rgb(156 163 175);
  pointer-events: none;
}
</style>
